import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select"
import { stripPresetConflicts } from "../lib/preset-utils"

/** Extract last segment from a bind path. E.g., "#{form.category}" -> "category" */
function extractFieldName(bind?: string): string | undefined {
  if (!bind) return undefined
  const stripped = bind.replace(/^#\{/, "").replace(/\}$/, "")
  const segments = stripped.split(".")
  return segments[segments.length - 1]
}

export function SelectRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("select")
  const resolvedVariant = (element as { variant?: string }).variant
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const placeholder = element.placeholder
    ? String(resolveSymbol(element.placeholder) ?? "")
    : undefined

  // Support bind for controlled value — wrap in #{} to resolve as state path
  const rawBound = element.bind
    ? resolveSymbol(element.bind.startsWith("#{") ? element.bind : `#{${element.bind}}`)
    : undefined
  // Fallback chain: bound value → element.defaultValue → "" (keeps Select controlled)
  const resolvedDefault = element.defaultValue
    ? String(resolveSymbol(String(element.defaultValue)) ?? "")
    : ""
  let boundValue = rawBound != null && rawBound !== "" ? String(rawBound) : resolvedDefault

  // base-ui SelectRoot.onValueChange signature accepts the Value type | null.
  // With an untyped generic, Value is inferred as `string | undefined`, so the
  // callback may receive `string | null | undefined`. Normalize to a string.
  const handleValueChange = (value: string | null | undefined) => {
    dispatchEvent(element.id, "change", { value: value ?? "" })
  }

  // Support both formats: element.options [{label,value}] and element.children [{type:"option"}]
  const rawOptions = (element as unknown as Record<string, unknown>).options
  // If options is a string expression (e.g. "#{ui.itemGroups}"), resolve it
  const resolvedOptions = typeof rawOptions === "string" ? resolveSymbol(rawOptions) : rawOptions
  const optionsFromProp = Array.isArray(resolvedOptions) ? resolvedOptions as Array<{label?: string; value?: string}> : undefined
  const childOptions = element.children?.filter(c => c.type === "option" || c.text) ?? []

  // Dynamic options from state: optionsData is a state path, valueKey/labelKey define mapping
  const el = element as unknown as Record<string, unknown>
  const dynamicData = el.optionsData ? resolveSymbol(el.optionsData as string) : undefined
  const dynamicOptions = Array.isArray(dynamicData) ? dynamicData as Record<string, unknown>[] : undefined
  const valueKey = String(el.valueKey ?? "value")
  const labelKey = String(el.labelKey ?? "label")

  // Merge: static options first, then dynamic options
  // Capture empty-value option labels to use as placeholder fallback
  let emptyOptionLabel: string | undefined
  const allOptions: Array<{label: string; value: string}> = []
  if (optionsFromProp) {
    for (const opt of optionsFromProp) {
      const v = String(opt.value ?? "")
      if (!v) {
        // Radix Select requires non-empty value, but capture the label as placeholder fallback
        emptyOptionLabel = String(resolveSymbol(opt.label ?? "") ?? "")
        continue
      }
      allOptions.push({ label: String(resolveSymbol(opt.label ?? "") ?? ""), value: v })
    }
  }
  if (dynamicOptions) {
    for (const item of dynamicOptions) {
      const v = String(item[valueKey] ?? "")
      const l = String(item[labelKey] ?? v)
      if (v) allOptions.push({ label: l, value: v })
    }
  }

  // When bound value is empty and static options exist, auto-select first option
  // This ensures dropdowns always show a meaningful label on initial render
  if (boundValue === "" && allOptions.length > 0 && !placeholder) {
    boundValue = allOptions[0].value
  }

  // Fallback placeholder: explicit placeholder > empty-value option label > first option label
  const effectivePlaceholder = placeholder
    ?? emptyOptionLabel
    ?? (allOptions.length > 0 ? allOptions[0].label : undefined)

  // base-ui Select.Root: pass `items` so Value auto-renders label for selected value
  const selectItems = allOptions.map(opt => ({ value: opt.value, label: opt.label }))

  // Controlled when element.bind is set (state-driven), uncontrolled otherwise
  // (base-ui manages internal state, fires onValueChange on selection)
  const valueProps = element.bind !== undefined
    ? { value: boundValue, onValueChange: handleValueChange }
    : { defaultValue: boundValue, onValueChange: handleValueChange }

  return (
    <Select {...valueProps} items={selectItems}>
      <SelectTrigger id={element.id} className={stripPresetConflicts(element.className)} data-action="select" data-field-name={extractFieldName(element.bind)} data-field-type="select" data-atom-type="select" data-variant={resolvedVariant} data-size={resolvedSize}>
        <SelectValue placeholder={effectivePlaceholder} />
      </SelectTrigger>
      <SelectContent>
        {allOptions.length > 0
          ? allOptions.map((opt, i) => (
              <SelectItem key={opt.value || i} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))
          : childOptions.map((opt, i) => {
              const text = String(resolveSymbol(opt.text ?? "") ?? "")
              return (
                <SelectItem key={opt.id || i} value={opt.variant || text}>
                  {text}
                </SelectItem>
              )
            })
        }
      </SelectContent>
    </Select>
  )
}
